import * as types from '../../../types';
import * as notify from '../../../../lang/en/notify';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';

const ShopifyOrderRepository = RepositoryFactory.get('acadia/shopify/orders/order');
const ShopifyOrderRefundRepository = RepositoryFactory.get('acadia/shopify/orders/refund');

export default {
  state: {
    lineItems: [], subtotalPrice: 0, totalTax: 0, totalPrice: 0, refund: null,
  }, mutations: {
    /**
     * Sets the line items in the payment state.
     * @param {Object} state - The current state.
     * @param {Array} lineItems - The line items to set.
     */
    [types.ORDER_SET_PAYMENT_LINE_ITEMS](state, lineItems) {
      state.lineItems = lineItems;
    }, /**
     * Sets the subtotal price in the payment state.
     * @param {Object} state - The current state.
     * @param {number} subtotalPrice - The subtotal price to set.
     */
    [types.ORDER_SET_PAYMENT_SUBTOTAL](state, subtotalPrice) {
      state.subtotalPrice = Number(subtotalPrice);
    }, /**
     * Sets the total tax in the payment state.
     * @param {Object} state - The current state.
     * @param {number} totalTax - The total tax to set.
     */
    [types.ORDER_SET_PAYMENT_TOTAL_TAX](state, totalTax) {
      state.totalTax = Number(totalTax);
    }, /**
     * Sets the total price in the payment state.
     * @param {Object} state - The current state.
     * @param {number} totalPrice - The total price to set.
     */
    [types.ORDER_SET_PAYMENT_TOTAL_PRICE](state, totalPrice) {
      state.totalPrice = Number(totalPrice);
    }, /**
     * Sets the refund in the payment state.
     * @param {Object} state - The current state.
     * @param {Object} refund - The refund to set.
     */
    [types.ORDER_SET_PAYMENT_REFUND](state, refund) {
      state.refund = refund;
    },
  }, actions: {
    /**
     * Initializes the payment state with the order details.
     * @param {Object} context - The Vuex context.
     * @param {Object} order - The order details.
     */
    [types.ORDER_PAYMENT_INIT]({commit}, order) {
      commit(types.ORDER_SET_PAYMENT_LINE_ITEMS, order.line_items);
      commit(types.ORDER_SET_PAYMENT_SUBTOTAL, order.subtotal_price);
      commit(types.ORDER_SET_PAYMENT_TOTAL_TAX, order.total_tax);
      commit(types.ORDER_SET_PAYMENT_TOTAL_PRICE, order.total_price);
    }, /**
     * Refreshes the payment details by fetching updated taxes and calculating the refund.
     * @param {Object} context - The Vuex context.
     */
    [types.ORDER_PAYMENT_REFRESH]({dispatch, commit, rootState, rootGetters, getters}) {
      commit(types.ORDER_SET_LOADING, true, {root: true});
      const {order: orderChange} = rootState;
      const {region, order, id: orderId} = orderChange;
      const draftOrder = rootGetters[types.ORDER_GET_DRAFT];

      return dispatch(types.ORDER_PAYMENT_FETCH_TAXES, {
        region, draftOrder, originalOrder: order
      }).then(response => {
        // If draft order creation fails, then do not update the order payment
        if (response.data) {
          dispatch(types.ORDER_PAYMENT_INIT, response.data);
        }

        return response.data;
      }).then(draftOrder => {
        commit(types.ORDER_SET_DRAFT_ORDER, draftOrder);

        return ShopifyOrderRefundRepository.calculateRefundDifference(region, orderId, {
          original_order: order, updated_order: draftOrder,
        });
      }).then(response => {
        return response.data.refund;
      }).then(refund => {
        commit(types.ORDER_SET_PAYMENT_REFUND, refund);
        dispatch(types.SUCCESS, notify.ORDER_REFUND_AVAILABLE);
      }).catch(error => {
        commit(types.ORDER_SET_SHIPPING_SELECTED, {price: 0}, {root: true});
        dispatch(types.ERROR, error);
        throw error;
      }).finally(() => {
        commit(types.ORDER_SET_LOADING, false, {root: true});
      });
    }, /**
     * Fetches the taxes for the draft order.
     * @param {Object} context - The Vuex context.
     * @param {Object} payload - The payload containing region, draftOrder, and originalOrder.
     * @returns {Promise} The response from the tax calculation request.
     */
    [types.ORDER_PAYMENT_FETCH_TAXES]({dispatch}, {region, draftOrder, originalOrder}) {
      const request = ShopifyOrderRepository.calculateTaxes(region, {
        draft_order: draftOrder, original_order: originalOrder
      });

      return request.then(response => {
        const {shipping_line, tax_lines} = response.data;

        // If the tax_lines do not exist, then warn the user...
        // sometimes we do not expect taxes
        if (!tax_lines || tax_lines.length < 1) {
          dispatch(types.WARNING, notify.TAX_LINES_ARE_EMPTY);
        }

        return response;
      }).catch((error) => {
        throw error;
      });
    },
  }, getters: {
    /**
     * Gets the total count of line items in the payment state.
     * @param {Object} state - The current state.
     * @returns {number} The total count of line items.
     */
    [types.ORDER_GET_PAYMENT_LINE_ITEMS_COUNT](state) {
      return state.lineItems.reduce((accumulator, currentValue) => {
        const quantity = currentValue.quantity ? Number(currentValue.quantity) : 0;
        return Number(accumulator) + quantity;
      }, 0);
    },
  },
};